import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import {
    Card, Button, Select, DatePicker, Form, Drawer, Tag, Space,
    Tooltip, Popconfirm, message, Table, Row, Col, Progress,
    Statistic, Badge, Input
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
    TeamOutlined, CalendarOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';

const { Option }     = Select;
const { RangePicker } = DatePicker;

// ── Constants ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    not_started: { color:'default',  label:'Not Started',  badge:'default'  },
    in_progress: { color:'blue',     label:'In Progress',  badge:'processing'},
    on_track:    { color:'green',    label:'On Track',     badge:'success'   },
    delayed:     { color:'red',      label:'Delayed',      badge:'error'     },
    completed:   { color:'cyan',     label:'Completed',    badge:'success'   },
    certified:   { color:'gold',     label:'Certified',    badge:'success'   },
    suspended:   { color:'orange',   label:'Suspended',    badge:'warning'   },
};

const getStatus = (val) => STATUS_CONFIG[val] || STATUS_CONFIG.not_started;

// ── Assignment Form Drawer ─────────────────────────────────────────────────
function AssignmentForm({ visible, onClose, onSaved, editRecord, frameworks, facilities, users }) {
    const [form]     = Form.useForm();
    const [saving,   setSaving]   = useState(false);
    const [bulkMode, setBulkMode] = useState(false);

    useEffect(() => {
        if (visible) {
            if (editRecord) {
                form.setFieldsValue({
                    ...editRecord,
                    dateRange: editRecord.start_date && editRecord.end_date
                        ? [moment(editRecord.start_date), moment(editRecord.end_date)] : null,
                    target_completion: editRecord.target_completion
                        ? moment(editRecord.target_completion) : null,
                });
            } else {
                form.resetFields();
                form.setFieldsValue({ status:'not_started' });
            }
            setBulkMode(false);
        }
    }, [visible, editRecord]); // eslint-disable-line

    const onFinish = async (values) => {
        setSaving(true);
        try {
            const dateRange        = values.dateRange || [];
            const start_date       = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
            const end_date         = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;
            const target_completion = values.target_completion
                ? values.target_completion.format('YYYY-MM-DD') : null;

            if (bulkMode && Array.isArray(values.facility_ids) && values.facility_ids.length > 1) {
                // Bulk assign
                const r = await MeService.bulkAssign({
                    framework_id:      values.framework_id,
                    facility_ids:      values.facility_ids,
                    start_date,
                    end_date,
                    target_completion,
                    supervisor_id:     values.supervisor_id,
                    reporting_period:  values.reporting_period,
                    period_label:      values.period_label,
                });
                if (r.data?.code !== 200) throw new Error(r.data?.message);
                message.success(r.data?.message || 'Bulk assignment done');
            } else {
                // Single assign
                const payload = {
                    ...values,
                    id:              editRecord?.id || null,
                    facility_id:     Array.isArray(values.facility_ids)
                                         ? values.facility_ids[0]
                                         : values.facility_id,
                    start_date,
                    end_date,
                    target_completion,
                };
                delete payload.facility_ids;
                delete payload.dateRange;
                const r = await MeService.saveAssignment(payload);
                if (r.data?.code !== 200) throw new Error(r.data?.message);
                message.success(editRecord ? 'Assignment updated' : 'Assignment created');
            }
            onSaved();
            onClose();
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <Drawer
            title={
                <span style={{ fontWeight:800 }}>
                    {editRecord ? '✏️ Edit Assignment' : '➕ Assign Framework'}
                </span>
            }
            open={visible} onClose={onClose} width={560} destroyOnClose
            footer={
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    {!editRecord && (
                        <Button onClick={() => setBulkMode(b => !b)}>
                            {bulkMode ? 'Single facility' : 'Bulk assign'}
                        </Button>
                    )}
                    <Button type="primary" loading={saving} onClick={() => form.submit()}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        {bulkMode ? 'Assign to All Selected' : (editRecord ? 'Update' : 'Assign')}
                    </Button>
                </div>
            }
        >
            {bulkMode && (
                <div style={{ marginBottom:12, padding:'8px 12px', background:'#e6f7ff',
                    border:'1px solid #91d5ff', borderRadius:6, fontSize:12, color:'#096dd9' }}>
                    📋 Bulk mode — select multiple facilities. The same framework will be assigned to all of them with the same dates and supervisor.
                </div>
            )}

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item name="framework_id" label="Framework" rules={[{ required:true }]}>
                    <Select showSearch optionFilterProp="children" placeholder="Select framework">
                        {frameworks.map(f => (
                            <Option key={f.id} value={f.id}>
                                <Tag color="blue" style={{ marginRight:4 }}>{f.code}</Tag>
                                {f.name}
                                <span style={{ fontSize:11, color:'#888', marginLeft:6 }}>({f.type})</span>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name={bulkMode ? 'facility_ids' : 'facility_id'}
                    label={bulkMode ? 'Facilities (select multiple)' : 'Facility'}
                    rules={[{ required:true }]}
                >
                    <Select
                        showSearch
                        mode={bulkMode ? 'multiple' : undefined}
                        optionFilterProp="children"
                        placeholder="Select facility..."
                        maxTagCount={4}
                    >
                        {facilities.map(f => (
                            <Option key={f.id} value={f.id}>
                                {f.business_name}
                                {f.located_region && <span style={{ fontSize:11, color:'#888' }}> — {f.located_region}</span>}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="dateRange" label="Implementation Period">
                    <RangePicker style={{ width:'100%' }} format="DD MMM YYYY"/>
                </Form.Item>

                <Form.Item name="target_completion" label="Target Completion Date">
                    <DatePicker style={{ width:'100%' }} format="DD MMM YYYY"
                        placeholder="When should full compliance be achieved?"/>
                </Form.Item>

                <Form.Item name="supervisor_id" label="M&E Supervisor / Assessor">
                    <Select showSearch optionFilterProp="children" placeholder="Assign supervisor" allowClear>
                        {users.map(u => (
                            <Option key={u.id} value={u.id}>{u.name} {u.email && `(${u.email})`}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="reporting_period" label="Reporting Period">
                            <Input placeholder="e.g. FY2025/26, Q1 2026"/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="period_label" label="Period Label">
                            <Input placeholder="e.g. Annual, Q1"/>
                        </Form.Item>
                    </Col>
                </Row>

                {!bulkMode && (
                    <Form.Item name="status" label="Status">
                        <Select>
                            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                                <Option key={val} value={val}>
                                    <Badge status={cfg.badge} text={cfg.label}/>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} placeholder="Any additional notes..."/>
                </Form.Item>
            </Form>
        </Drawer>
    );
}

// ── Assignment Detail Drawer ───────────────────────────────────────────────
function AssignmentDetail({ visible, onClose, assignment }) {
    if (!assignment) return null;
    const st = getStatus(assignment.status);
    return (
        <Drawer
            title={<span style={{ fontWeight:800 }}>📋 Assignment Detail</span>}
            open={visible} onClose={onClose} width={480}
        >
            <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:800, fontSize:16, color:'#1f3864', marginBottom:4 }}>
                    {assignment.framework_name}
                </div>
                <Tag color="blue">{assignment.framework_code}</Tag>
                <Tag>{assignment.framework_type}</Tag>
                <Badge status={st.badge} text={st.label} style={{ marginLeft:8 }}/>
            </div>

            {[
                { label:'Facility',    value: assignment.facility_name },
                { label:'Region',      value: assignment.region },
                { label:'Supervisor',  value: assignment.supervisor_name },
                { label:'Assigned To', value: assignment.assigned_to_name },
                { label:'Start Date',  value: assignment.start_date ? moment(assignment.start_date).format('DD MMM YYYY') : null },
                { label:'End Date',    value: assignment.end_date ? moment(assignment.end_date).format('DD MMM YYYY') : null },
                { label:'Target',      value: assignment.target_completion ? moment(assignment.target_completion).format('DD MMM YYYY') : null },
                { label:'Period',      value: assignment.period_label || assignment.reporting_period },
            ].filter(r => r.value).map(r => (
                <div key={r.label} style={{ display:'flex', gap:8, padding:'8px 0',
                    borderBottom:'1px solid #f0f0f0', fontSize:13 }}>
                    <span style={{ color:'#888', width:110, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontWeight:600 }}>{r.value}</span>
                </div>
            ))}

            <div style={{ marginTop:20 }}>
                <div style={{ fontSize:12, color:'#888', marginBottom:6 }}>Overall Completion</div>
                <Progress
                    percent={parseFloat(assignment.completion_pct || 0)}
                    strokeColor={
                        assignment.completion_pct >= 75 ? '#52c41a' :
                        assignment.completion_pct >= 50 ? '#1890ff' :
                        assignment.completion_pct >= 25 ? '#fa8c16' : '#f5222d'
                    }
                    format={p => <span style={{ fontWeight:800 }}>{p}%</span>}
                />
            </div>

            {assignment.notes && (
                <div style={{ marginTop:16, padding:12, background:'#f8faff', borderRadius:8 }}>
                    <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Notes</div>
                    <div style={{ fontSize:13 }}>{assignment.notes}</div>
                </div>
            )}
        </Drawer>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function MeAssignments() {
    const [assignments,  setAssignments]  = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('me_assign');
    const canEdit   = util.canEdit('me_assign');
    const canDelete = util.canDelete('me_assign');
    const canExport = util.canExport('me_assign');
    const [frameworks,   setFrameworks]   = useState([]);
    const [facilities,   setFacilities]   = useState([]);
    const [users,        setUsers]        = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [formVisible,  setFormVisible]  = useState(false);
    const [editRecord,   setEditRecord]   = useState(null);
    const [viewRecord,   setViewRecord]   = useState(null);
    const [filterFw,     setFilterFw]     = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterRegion, setFilterRegion] = useState('');
    const [search,       setSearch]       = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [aRes, fwRes, facRes] = await Promise.all([
                MeService.getAssignments({
                    ...(filterFw     ? { framework_id: filterFw }   : {}),
                    ...(filterStatus ? { status: filterStatus }      : {}),
                    ...(filterRegion ? { region: filterRegion }      : {}),
                }),
                MeService.getFrameworks({ status:'active' }),
                FacilityService.all({ status:1 }),
            ]);
            setAssignments(aRes.data?.data  || []);
            setFrameworks(fwRes.data?.data   || []);
            setFacilities(facRes.data?.result?.data || []);
        } catch(e) { message.error('Failed to load assignments'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filterFw, filterStatus, filterRegion]); // eslint-disable-line

    const handleEdit   = (rec) => { setEditRecord(rec); setFormVisible(true); };
    const handleView   = (rec) => setViewRecord(rec);

    const handleDelete = async (id) => {
        try {
            const r = await MeService.deleteAssignment(id);
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success('Assignment deleted');
            load();
        } catch(e) { message.error(e.message); }
    };

    // Summary stats
    const total     = assignments.length;
    const onTrack   = assignments.filter(a => a.status === 'on_track' || a.status === 'completed' || a.status === 'certified').length;
    const delayed   = assignments.filter(a => a.status === 'delayed').length;
    const avgCompl  = total ? Math.round(assignments.reduce((s,a) => s + parseFloat(a.completion_pct||0), 0) / total) : 0;

    const regions = [...new Set(assignments.map(a => a.region).filter(Boolean))];

    const filtered = assignments.filter(a =>
        !search ||
        (a.facility_name  || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.framework_name || '').toLowerCase().includes(search.toLowerCase())
    );

    const columns = [
        { title:'Framework', dataIndex:'framework_name', render:(v, rec) => (
            <div>
                <div style={{ fontWeight:700, color:'#1f3864' }}>{v}</div>
                <Tag color="blue" style={{ fontSize:10 }}>{rec.framework_code}</Tag>
                <Tag style={{ fontSize:10 }}>{rec.framework_type}</Tag>
            </div>
        )},
        { title:'Facility', dataIndex:'facility_name', render:(v, rec) => (
            <div>
                <div style={{ fontWeight:600 }}>{v}</div>
                {rec.region && <div style={{ fontSize:11, color:'#888' }}>{rec.region}</div>}
            </div>
        )},
        { title:'Supervisor', dataIndex:'supervisor_name', width:140,
            render: v => v || <span style={{ color:'#ccc' }}>—</span> },
        { title:'Period', dataIndex:'period_label', width:100,
            render:(v, rec) => v || rec.reporting_period || '—' },
        { title:'Target', dataIndex:'target_completion', width:110,
            render: v => v ? moment(v).format('MMM YYYY') : '—' },
        { title:'Progress', dataIndex:'completion_pct', width:140,
            render: v => {
                const pct = parseFloat(v || 0);
                return (
                    <div>
                        <Progress size="small" percent={pct}
                            strokeColor={pct>=75?'#52c41a':pct>=50?'#1890ff':pct>=25?'#fa8c16':'#f5222d'}
                            format={p=><span style={{ fontSize:11 }}>{p}%</span>}/>
                    </div>
                );
            }
        },
        { title:'Status', dataIndex:'status', width:120,
            render: v => {
                const st = getStatus(v);
                return <Badge status={st.badge} text={st.label}/>;
            }
        },
        { title:'Actions', width:120, render:(_, rec) => (
            <Space size="small">
                <Tooltip title="View">
                    <Button size="small" icon={<EyeOutlined/>} onClick={() => handleView(rec)}/>
                </Tooltip>
                <Tooltip title="Edit">
                    <Button size="small" icon={<EditOutlined/>} onClick={() => handleEdit(rec)}/>
                </Tooltip>
                <Popconfirm title="Delete assignment?" onConfirm={() => handleDelete(rec.id)}
                    okButtonProps={{ danger:true }}>
                    <Button size="small" icon={<DeleteOutlined/>} danger/>
                </Popconfirm>
            </Space>
        )},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>🏥 Framework Assignments</h4>
                    <small className="text-muted">Assign M&E frameworks to facilities with timelines</small>
                </div>
                <div className="my-auto ml-auto">
                    <Button type="primary" icon={<PlusOutlined/>}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}
                        onClick={() => { setEditRecord(null); setFormVisible(true); }}>
                        Assign Framework
                    </Button>
                </div>
            </div>

            <div className="page-pad">
                {/* KPI strip */}
                <Row gutter={12} style={{ marginBottom:16 }}>
                    {[
                        { label:'Total Assignments', val:total,    color:'#1890ff' },
                        { label:'On Track',          val:onTrack,  color:'#52c41a' },
                        { label:'Delayed',           val:delayed,  color:'#f5222d' },
                        { label:'Avg Completion',    val:`${avgCompl}%`, color:'#7c3aed' },
                    ].map(k => (
                        <Col key={k.label} xs={12} sm={6}>
                            <Card size="small" style={{ textAlign:'center', borderLeft:`4px solid ${k.color}` }}>
                                <Statistic title={k.label} value={k.val}
                                    valueStyle={{ color:k.color, fontSize:20, fontWeight:800 }}/>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Filters */}
                <Card size="small" style={{ marginBottom:16 }}>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                        <Input.Search
                            placeholder="Search facility or framework..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            style={{ width:240 }} allowClear/>
                        <Select value={filterFw} onChange={setFilterFw}
                            style={{ width:200 }} allowClear placeholder="All frameworks">
                            {frameworks.map(f => (
                                <Option key={f.id} value={f.id}>{f.code} — {f.name}</Option>
                            ))}
                        </Select>
                        <Select value={filterStatus} onChange={setFilterStatus}
                            style={{ width:150 }} allowClear placeholder="All statuses">
                            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                                <Option key={val} value={val}>{cfg.label}</Option>
                            ))}
                        </Select>
                        <Select value={filterRegion} onChange={setFilterRegion}
                            style={{ width:150 }} allowClear placeholder="All regions">
                            {regions.map(r => <Option key={r} value={r}>{r}</Option>)}
                        </Select>
                    </div>
                </Card>

                {/* Table */}
                <Card size="small">
                    <Table
                        size="small"
                        dataSource={filtered}
                        rowKey="id"
                        columns={columns}
                        loading={loading}
                        pagination={{ pageSize:20, showSizeChanger:true, showTotal: t => `${t} assignments` }}
                        expandable={{
                            expandedRowRender: rec => (
                                <div style={{ padding:'8px 16px' }}>
                                    {rec.notes && <div style={{ fontSize:12, color:'#555' }}>📝 {rec.notes}</div>}
                                    <div style={{ fontSize:12, color:'#888', marginTop:4 }}>
                                        Started: {rec.start_date ? moment(rec.start_date).format('DD MMM YYYY') : '—'}
                                        {rec.end_date && ` · Ends: ${moment(rec.end_date).format('DD MMM YYYY')}`}
                                        {rec.assigned_to_name && ` · Assigned to: ${rec.assigned_to_name}`}
                                    </div>
                                </div>
                            ),
                        }}
                    />
                </Card>
            </div>

            {/* Assignment form */}
            <AssignmentForm
                visible={formVisible}
                onClose={() => setFormVisible(false)}
                onSaved={load}
                editRecord={editRecord}
                frameworks={frameworks}
                facilities={facilities}
                users={users}
            />

            {/* Detail drawer */}
            <AssignmentDetail
                visible={!!viewRecord}
                onClose={() => setViewRecord(null)}
                assignment={viewRecord}
            />
        </div>
    );
}


